import { Form } from "antd";
import { useEffect, useState } from "react";
import DynamicForm from "../../controls/DynamicForm";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { getProblemMessage } from "../../utils/http";
import {
  addBundleAssignment,
  updateBundleAssignment,
  deleteBundlesAssignment,
} from "../../repository/data.repository";
import { BundleAssignmentForm } from "./BundlesAssignmentsFormDialog.default";
import { BundleAssignment } from "../../repository/data.default";
import {
  companiesRequestWithTenant,
  bundlesRequest,
} from "../bundles/bundleFilters";
import dayjs from "dayjs";

export interface BundlesAssignmentsFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete" | "view";
  readonly initialValues?: BundleAssignment;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function BundlesAssignmentsFormDialog(props: BundlesAssignmentsFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<BundleAssignmentForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function onError(e: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(e, "Something went wrong, try again"));
  }

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue(props.initialValues);
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { company, bundle, allocatedSubmissions } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addBundleAssignment({
        companyUid: company.value,
        bundleUid: bundle.value,
        allocatedSubmissions,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updateBundleAssignment({
        uid: props.initialValues?.uid,
        allocatedSubmissions: allocatedSubmissions ?? null,
        rowVersion: props.initialValues?.rowVersion,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteBundlesAssignment(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(null);
    form.resetFields();
  }

  // Allocation is optional; if provided it must be a non-negative whole number (mirrors the
  // Test Groups "Allowed submissions" integer rule).
  const allocationRules = [
    {
      validator: (_: unknown, value: unknown) => {
        if (value == null || value === "") return Promise.resolve();
        const n = Number(value);
        return Number.isInteger(n) && n >= 0
          ? Promise.resolve()
          : Promise.reject(
              new Error("Allocation must be a non-negative whole number"),
            );
      },
    },
  ];

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Assignment", undefined)}
      onCancel={onCancel}
      actions={
        props.mode != "view" && (
          <ModalFooter
            mode={props.mode}
            loading={loading}
            valid={props.mode === "add" ? valid : true}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        )
      }
    >
      {props.mode === "add" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: companiesRequestWithTenant,
                    rules: [{ required: true, message: "Company is required" }],
                  },
                  {
                    type: "combobox",
                    name: "bundle",
                    label: "Bundle",
                    request: bundlesRequest,
                    rules: [{ required: true, message: "Bundle is required" }],
                  },
                  {
                    type: "numberbox",
                    name: "allocatedSubmissions",
                    label: "Allocated submissions",
                    tooltip:
                      "Soft quota — the number of submissions this company is expected to use for the bundle. It's metered, not enforced: usage can exceed it (flagged for billing), it never blocks submissions.",
                    step: "1",
                    rules: allocationRules,
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "update" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  { type: "labelbox", label: "Company", name: "companyName" },
                  { type: "labelbox", label: "Bundle", name: "bundleName" },
                  {
                    type: "labelbox",
                    label: "Consumed submissions",
                    name: "consumedSubmissions",
                  },
                  {
                    type: "numberbox",
                    name: "allocatedSubmissions",
                    label: "Allocated submissions",
                    tooltip:
                      "Soft quota — the number of submissions this company is expected to use for the bundle. It's metered, not enforced: usage can exceed it (flagged for billing), it never blocks submissions.",
                    step: "1",
                    rules: allocationRules,
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "view" && (
        <DynamicForm
          form={form}
          preview
          schema={{
            groups: [
              {
                controls: [
                  { type: "labelbox", label: "Company", name: "companyName" },
                  { type: "labelbox", label: "Bundle", name: "bundleName" },
                  {
                    type: "labelbox",
                    label: "Allocated submissions",
                    name: "allocatedSubmissions",
                  },
                  {
                    type: "labelbox",
                    label: "Consumed submissions",
                    name: "consumedSubmissions",
                  },
                  {
                    type: "labelbox",
                    label: "Created By",
                    name: "createdByUserUserName",
                  },
                  {
                    type: "labelbox",
                    label: "Created On",
                    name: "createdOn",
                    format: (value) => dayjs(value).format("DD/MM/YYYY HH:mm"),
                  },
                  {
                    type: "labelbox",
                    label: "Updated On",
                    name: "updatedOn",
                    format: (value) => dayjs(value).format("DD/MM/YYYY HH:mm"),
                  },
                  {
                    type: "labelbox",
                    label: "Updated By",
                    name: "updatedByUserUserName",
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="assignment"
          entityName={props.initialValues?.bundleName}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default BundlesAssignmentsFormDialog;
